import { Rocket } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/cn';
import { useUpdateAvailable } from '@/hooks/useUpdateAvailable';
import { applyUpdate } from '@/lib/version-monitor';

interface UpdateAvailableNavItemProps {
  collapsed: boolean;
}

/**
 * Sidebar item that appears in the warning color only when CloudFront
 * starts serving a newer build than the one this tab booted with. Clicking
 * reloads the page to pick up the new bundle.
 *
 * Wired into the console sidebar via the `render` escape hatch on
 * `NavItem` so it can be conditionally rendered without complicating the
 * shared `NavMain` component (see `src/components/navigation/console-nav.tsx`).
 */
export function UpdateAvailableNavItem({ collapsed }: UpdateAvailableNavItemProps) {
  const updateAvailable = useUpdateAvailable();
  if (!updateAvailable) return null;

  const button = (
    <button
      type="button"
      onClick={applyUpdate}
      className={cn(
        'group flex w-full items-center gap-3 rounded-md py-2 text-sm font-medium transition-colors',
        'text-status-warning-fg hover:bg-status-warning-bg',
        collapsed ? 'justify-center px-2' : 'px-3',
      )}
    >
      <Rocket className="h-5 w-5 shrink-0" />
      {!collapsed && <span className="flex-1 text-left">Update available</span>}
    </button>
  );

  if (collapsed) {
    return (
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent side="right">
          <p>Update available</p>
          <p className="text-xs text-muted-foreground">Click to refresh</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  return button;
}
